declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag: (...args: unknown[]) => void;
	}
}

export const gtag = (...args: unknown[]): void => {
	if (typeof window !== 'undefined') {
		if (typeof window.gtag === 'undefined') {
			window.dataLayer = window.dataLayer ?? [];
			// oxlint-disable-next-line consistent-function-scoping
			window.gtag = function gtag() {
				window.dataLayer?.push(arguments);
			};
		}

		window.gtag(...args);
	}
};

/** Whether {@link loadGtag} has already put the loader in the document, per browser session. */
let gtagLoaderRequested = false;

/**
 * Injects the Google Analytics loader, imperatively and only after the document has hydrated.
 *
 * It is deliberately NOT declared in the framework's `head()`, because a `<script src>` there is the
 * one head element React can fail on. React exempts inline scripts from its hydration checks (they
 * carry `dangerouslySetInnerHTML` + `suppressHydrationWarning`), but a script with a `src` and no
 * children gets no exemption: React runs its leftover-children check on it, and if anything has shifted
 * the head by a position it is paired with an inline script whose text node then counts as an
 * unconsumed child — React #418 `args[]=HTML`, whole tree regenerated. In production the shift came
 * from a third party inserting a `<script>` into the head before hydration. Loading the tag from here
 * keeps the hydrated head inline-only, so neither an injected script nor a blocked analytics request
 * can cost anything beyond analytics itself.
 *
 * Callers need no ordering guarantee: {@link gtag} queues into `dataLayer` whether or not the loader
 * has arrived, gtag.js drains that queue when it does, and if it never arrives the queue is simply
 * dropped. Safe to call more than once, and a no-op without a measurement id or a document.
 *
 * **Call this only once the donor has consented to analytics.** That queue-and-drop behaviour is what makes
 * the consent gate work at a single point: both apps leave their `config` and `page_view` calls unguarded
 * and gate this function alone, so denying — or never answering — leaves the queue undrained and contacts
 * Google not at all. Loading it unconditionally and relying on Consent Mode's denied defaults to make the
 * traffic harmless is what an external privacy audit rejected; see the callers in each app's `__root.tsx`.
 */
export const loadGtag = (measurementId: string): void => {
	if (typeof document === 'undefined' || !measurementId || gtagLoaderRequested) return;
	gtagLoaderRequested = true;
	const script = document.createElement('script');
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
	script.async = true;
	document.head.appendChild(script);
};

/**
 * Query-string parameters that must never be reported to analytics because they
 * carry PII or credentials: a donor's email address, a password-reset session
 * key, or a redirect target that itself embeds those values.
 */
const SENSITIVE_ANALYTICS_PARAMS = new Set(['email', 'redirect', 'session', 'token', 'access_token', 'password']);

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

/**
 * Redacts PII and credentials from a URL before it is sent to Google Analytics
 * as `page_location`.
 *
 * Donor emails and password-reset keys travel through the SPA query string
 * (e.g. `/confirm/code?email=donor@example.com`), so reporting the raw
 * `location.href` would leak them to a third party. This keeps the path and the
 * benign analytics params intact while redacting any sensitive value — both by
 * a known-sensitive parameter name and by an email-shaped catch-all that also
 * covers values nested inside a `redirect` param.
 */
export const sanitizeAnalyticsUrl = (href: string): string => {
	try {
		const url = new URL(href);
		const keysToRedact: string[] = [];
		url.searchParams.forEach((value, key) => {
			if (SENSITIVE_ANALYTICS_PARAMS.has(key.toLowerCase()) || EMAIL_PATTERN.test(value)) {
				keysToRedact.push(key);
			}
		});
		for (const key of keysToRedact) {
			url.searchParams.set(key, 'redacted');
		}
		return url.toString();
	} catch {
		// A malformed URL should never take analytics (or the app) down; drop the
		// query string entirely so nothing sensitive can slip through.
		return href.split('?')[0] ?? href;
	}
};
